// The hard paywall (iOS TrailBivvy/Features/Paywall/PaywallScreen.swift, DECISION.md #38, S2–S8), selling TrailBivvy
// Pro through Stripe Checkout. Shows what the subscription includes (real features only), the price as the most
// prominent element, the renewal and payment terms, the Terms of Sale and Privacy Policy, Restore, and Safety tools
// that always work without a subscription. No urgency, no countdown, no crossed-out price (iOS S7).

import { useEffect, useState } from 'preact/hooks';
import { locale, t, translate } from '../../i18n';
import { ApiError, subscriptionApi, type PlanAnswer } from '../../store/api';
import { formatPrice, formatTrialLength, type PaywallPhase } from '../../store/paywall';
import { Icon, type IconName } from '../components/Icon';
import { Banner, Button, Spinner } from '../components/ui';
import { useEnv } from '../env';
import { href, route } from '../router';

const appIcon = '/icons/icon-192.png';

type PlanState = { kind: 'loading' } | { kind: 'error' } | { kind: 'plan'; plan: PlanAnswer };

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The legal pages exist in English and French (LyteFast is a French company: decisions.md PWA-48). */
export function legalPath(page: 'terms' | 'privacy'): string {
  return locale.value === 'fr' ? `/legal/fr/${page}` : `/legal/${page}`;
}

export function PaywallValueList() {
  const values: Array<[IconName, string, string]> = [
    ['moon', t('common.sleepTonight'), t('paywall.value.tonight.detail.v3')],
    ['map', t('paywall.value.maps.title'), t('paywall.value.maps.detail')],
    ['route', t('paywall.value.catalog.title'), t('paywall.value.catalog.detail')],
    ['hiker', t('paywall.value.navigation.title'), t('pwa.paywall.value.navigation.detail')],
    ['safety', t('paywall.value.safety.title'), t('paywall.value.safety.detail')],
    ['globe', t('paywall.value.languages.title'), t('paywall.value.languages.detail')],
    ['eyeOff', t('paywall.value.privacy.title'), t('pwa.paywall.value.privacy.detail')],
  ];
  return (
    <ul class="value-list" aria-label={t('subscription.included')}>
      {values.map(([icon, title, detail]) => (
        <li key={title}>
          <Icon name={icon} />
          <div>
            <p class="headline">{title}</p>
            <p class="footnote">{detail}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function LegalLinks() {
  return (
    <nav class="links" aria-label={t('pwa.legal.terms')}>
      <a href={legalPath('terms')} target="_blank" rel="noopener">
        {t('pwa.legal.terms')}
      </a>
      <a href={legalPath('privacy')} target="_blank" rel="noopener">
        {t('pwa.legal.privacy')}
      </a>
    </nav>
  );
}

export function PaywallScreen({ phase }: { phase: PaywallPhase }) {
  const env = useEnv();
  const [plan, setPlan] = useState<PlanState>({ kind: 'loading' });
  const [email, setEmail] = useState(env.prefs.subscriptionEmail.value ?? '');
  const [emailError, setEmailError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const cancelled = route.value.query.get('checkout') === 'cancelled';
  const details = env.entitlement.details.value;

  const load = async () => {
    setPlan({ kind: 'loading' });
    try {
      setPlan({ kind: 'plan', plan: await subscriptionApi.plans() });
    } catch {
      setPlan({ kind: 'error' });
    }
  };

  useEffect(() => {
    if (phase === 'locked') void load();
  }, [phase]);

  const subscribe = async (e: Event) => {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!emailPattern.test(address)) {
      setEmailError(true);
      return;
    }
    setEmailError(false);
    setError(undefined);
    setBusy(true);
    env.prefs.subscriptionEmail.value = address;
    try {
      const { url } = await subscriptionApi.checkout(address, locale.value);
      window.location.assign(url);
    } catch (err) {
      setBusy(false);
      const code = err instanceof ApiError ? err.code : 'unknown';
      if (code === 'already_subscribed') setError('alreadySubscribed');
      else if (code === 'not_offered') setError('notOffered');
      else if (err instanceof ApiError && err.isNetwork) setError('offline');
      else setError('checkoutFailed');
    }
  };

  const safetyButton = (
    <Button variant="secondary" icon="safety" onClick={() => (env.safetyOpen.value = true)}>
      {t('paywall.safety')}
    </Button>
  );

  return (
    <div class="layer" role="dialog" aria-modal="true" aria-labelledby="paywall-title">
      <div class="paywall">
        <header class="paywall-hero">
          <img src={appIcon} alt="" width={96} height={96} />
          <h1 id="paywall-title">{t('paywall.title')}</h1>
          <p>{t('pwa.paywall.subtitle')}</p>
        </header>
        <div class="paywall-body">
          {phase === 'checking' ? (
            <div class="price-panel">
              <Spinner label={t('paywall.checking')} />
              {safetyButton}
            </div>
          ) : (
            <div class="price-panel">
              {details?.status === 'billingRetry' && (
                <Banner tone="caution">
                  <p>{t('pwa.paywall.billingProblem')}</p>
                  <a href={href('more/pro')}>{t('subscription.manage')}</a>
                </Banner>
              )}
              {details && details.status !== 'billingRetry' && <p class="footnote">{t('pwa.paywall.ended')}</p>}
              {cancelled && <Banner tone="info">{t('pwa.paywall.cancelled')}</Banner>}
              <PlanPanel state={plan} retry={load} />
              {plan.kind === 'plan' && plan.plan.available && (
                <form class="stack" onSubmit={subscribe} noValidate>
                  <div class="field">
                    <label for="paywall-email">{t('pwa.paywall.email.label')}</label>
                    <input
                      id="paywall-email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="off"
                      spellcheck={false}
                      required
                      value={email}
                      aria-invalid={emailError || undefined}
                      aria-describedby="paywall-email-hint"
                      onInput={(e) => setEmail((e.currentTarget as HTMLInputElement).value)}
                    />
                    {emailError ? (
                      <span class="error" role="alert">
                        {t('pwa.paywall.email.invalid')}
                      </span>
                    ) : (
                      <span class="hint" id="paywall-email-hint">
                        {t('pwa.paywall.email.hint')}
                      </span>
                    )}
                  </div>
                  {error && (
                    <Banner tone={error === 'alreadySubscribed' ? 'info' : 'negative'} role="alert">
                      <p>{error === 'offline' ? t('pwa.error.offline') : translate(`pwa.paywall.${error}`)}</p>
                      {error === 'alreadySubscribed' && <a href={href('restore', { email })}>{t('pwa.restore.title')}</a>}
                    </Banner>
                  )}
                  <Button variant="primary" type="submit" busy={busy} icon="lock">
                    {t('paywall.button.subscribe')}
                  </Button>
                  <Disclosures plan={plan.plan} />
                </form>
              )}
              <Button variant="plain" href={href('restore')}>
                {t('pwa.paywall.restore')}
              </Button>
              {safetyButton}
              <p class="footnote" style={{ textAlign: 'center' }}>
                {t('paywall.safety.hint')}
              </p>
            </div>
          )}
          <PaywallValueList />
          <LegalLinks />
        </div>
      </div>
    </div>
  );
}

function PlanPanel({ state, retry }: { state: PlanState; retry: () => void }) {
  if (state.kind === 'loading') return <Spinner label={t('pwa.paywall.loading')} />;
  if (state.kind === 'error') {
    return (
      <div class="stack-s">
        <Banner tone="caution">{t('pwa.paywall.unavailable')}</Banner>
        <Button icon="refresh" onClick={retry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  const plan = state.plan;
  if (!plan.available) {
    return <Banner tone="info">{t(plan.reason === 'notOffered' ? 'pwa.paywall.notOffered' : 'pwa.paywall.notConfigured')}</Banner>;
  }
  const price = formatPrice(plan.unitAmount ?? 0, plan.currency ?? 'usd', locale.value);
  const yearly = plan.interval === 'year' && (plan.intervalCount ?? 1) === 1;
  return (
    <div class="stack-s">
      <p class="price-line">{yearly ? t('paywall.price.perYear', price) : price}</p>
      {(plan.trialDays ?? 0) > 0 && (
        <p class="footnote" style={{ textAlign: 'center' }}>
          {t('pwa.paywall.trialNote', formatTrialLength(plan.trialDays!, locale.value))}
        </p>
      )}
    </div>
  );
}

const slot = '\u{E000}';

function Disclosures({ plan }: { plan: PlanAnswer }) {
  const [before, after = ''] = t('pwa.paywall.consent', slot).split(slot);
  return (
    <div class="stack-s">
      <p class="footnote">
        {before}
        <a href={legalPath('terms')} target="_blank" rel="noopener">
          {t('pwa.legal.terms')}
        </a>
        {after}
      </p>
      <p class="footnote">{t('pwa.paywall.disclosure.renewal')}</p>
      {(plan.trialDays ?? 0) > 0 && <p class="footnote">{t('pwa.paywall.disclosure.trialPayment')}</p>}
      <p class="footnote">{t('pwa.paywall.disclosure.refund')}</p>
      <p class="footnote">
        {t('pwa.paywall.currencyNote')} {t('pwa.paywall.stripe')}
      </p>
    </div>
  );
}
